import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { EyeIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import { useSelectedCourse } from '../../contexts/SelectedCourseContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Inscripcion {
  id: number;
  alumno: {
    id: number;
    numeroAlumno: string;
    nombre: string;
    apellido: string;
    email: string;
  };
  curso: {
    id: number;
    nombre: string;
    codigo: string;
    anio?: number;
  };
  turno: {
    id: number;
    nombre: string;
    dia: string;
    horaInicio: string;
    horaFin: string;
  };
  estado: 'activa' | 'cancelada' | 'finalizada';
  observaciones?: string;
  createdAt: string;
  updatedAt: string;
}

interface Alumno {
  id: number;
  numeroAlumno: string;
  nombre: string;
  apellido: string;
  email: string;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  turnos?: Turno[];
}

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
  cupoOcupado: number;
  cupoMaximo: number;
  inscripcionesAbiertas: boolean;
}

const InscripcionesPage: React.FC = () => {
  const [showDetails, setShowDetails] = useState<Inscripcion | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [alumnosEncontrados, setAlumnosEncontrados] = useState<Alumno[]>([]);
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState<Alumno | null>(null);
  const [cursoSeleccionado, setCursoSeleccionado] = useState<Curso | null>(null);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState<Turno | null>(null);
  const [errorBusqueda, setErrorBusqueda] = useState('');
  const [filtroCursoId, setFiltroCursoId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { selectedCourse, courses } = useSelectedCourse();
  const { user } = useAuth();

  // Sincronizar filtro local cuando cambia el curso del header
  React.useEffect(() => {
    if (selectedCourse) {
      setFiltroCursoId(selectedCourse.id);
    }
  }, [selectedCourse]);

  // Fetch inscripciones (filtradas por curso local)
  const { data: inscripciones, isLoading, error } = useQuery({
    queryKey: ['inscripciones', filtroCursoId],
    queryFn: async () => {
      const response = await axios.get('/inscripciones');
      let data = response.data;
      
      if (filtroCursoId) {
        data = data.filter((insc: Inscripcion) => insc.curso.id === filtroCursoId);
      }
      
      return data;
    }
  });

  // Cancelar inscripción
  const cancelarInscripcionMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.post(`/inscripciones/${id}/cancelar`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inscripciones'] });
    }
  });

  // Buscar alumno (búsqueda flexible)
  const buscarAlumnoMutation = useMutation({
    mutationFn: async (termino: string) => {
      const response = await axios.get(`/alumnos/buscar-flexible?termino=${encodeURIComponent(termino)}`);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.alumnos && data.alumnos.length > 0) {
        setAlumnosEncontrados(data.alumnos);
        setErrorBusqueda('');
        // Si hay un solo resultado, seleccionarlo automáticamente
        if (data.alumnos.length === 1) {
          setAlumnoSeleccionado(data.alumnos[0]);
          setAlumnosEncontrados([]);
        }
      } else {
        setErrorBusqueda('No se encontraron alumnos. Intenta con número de legajo, apellido o nombre.');
        setAlumnosEncontrados([]);
        setAlumnoSeleccionado(null);
      }
    },
    onError: (error: any) => {
      console.error('Error al buscar alumno:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al buscar el alumno';
      setErrorBusqueda(errorMsg);
      setAlumnosEncontrados([]);
      setAlumnoSeleccionado(null);
    }
  });

  // Obtener TODOS los turnos del curso seleccionado (no solo los que tienen inscripciones abiertas)
  const { data: turnosDisponibles } = useQuery({
    queryKey: ['turnos-curso', cursoSeleccionado?.id],
    queryFn: async () => {
      if (!cursoSeleccionado) return [];
      const response = await axios.get(`/turnos/curso/${cursoSeleccionado.id}`);
      return response.data;
    },
    enabled: !!cursoSeleccionado
  });

  // Crear inscripción como administrador (permite crear incluso si las inscripciones están cerradas)
  const crearInscripcionMutation = useMutation({
    mutationFn: async (data: { numeroAlumno: string; cursoId: number; turnoId: number }) => {
      const response = await axios.post('/inscripciones/admin', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inscripciones'] });
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
      queryClient.invalidateQueries({ queryKey: ['alumnos-inscritos'] }); // Invalidar para que se recarguen en la página de Notas
      queryClient.invalidateQueries({ queryKey: ['turnos-curso'] }); // Invalidar turnos del curso
      setShowCreateModal(false);
      resetForm();
      alert('Inscripción creada exitosamente');
    },
    onError: (error: any) => {
      console.error('Error al crear inscripción:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al crear la inscripción';
      alert(`Error: ${errorMessage}`);
    }
  });

  const resetForm = () => {
    setTerminoBusqueda('');
    setAlumnosEncontrados([]);
    setAlumnoSeleccionado(null);
    setCursoSeleccionado(null);
    setTurnoSeleccionado(null);
    setErrorBusqueda('');
  };

  const handleBuscarAlumno = (e: React.FormEvent) => {
    e.preventDefault();
    if (terminoBusqueda.trim()) {
      buscarAlumnoMutation.mutate(terminoBusqueda.trim());
    }
  };

  const handleSeleccionarAlumno = (alumno: Alumno) => {
    setAlumnoSeleccionado(alumno);
    setAlumnosEncontrados([]);
    setTerminoBusqueda(`${alumno.apellido}, ${alumno.nombre} (${alumno.numeroAlumno})`);
  };

  const handleCrearInscripcion = () => {
    if (!alumnoSeleccionado) {
      alert('Por favor, selecciona un alumno');
      return;
    }
    if (!cursoSeleccionado) {
      alert('Por favor, selecciona un curso');
      return;
    }
    if (!turnoSeleccionado) {
      alert('Por favor, selecciona un turno');
      return;
    }

    console.log('Creando inscripción:', {
      numeroAlumno: alumnoSeleccionado.numeroAlumno,
      cursoId: cursoSeleccionado.id,
      turnoId: turnoSeleccionado.id
    });

    crearInscripcionMutation.mutate({
      numeroAlumno: alumnoSeleccionado.numeroAlumno,
      cursoId: cursoSeleccionado.id,
      turnoId: turnoSeleccionado.id
    });
  };

  // Filtrar inscripciones por búsqueda
  const filteredInscripciones = inscripciones?.filter((inscripcion: Inscripcion) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      inscripcion.alumno.nombre.toLowerCase().includes(searchLower) ||
      inscripcion.alumno.apellido.toLowerCase().includes(searchLower) ||
      inscripcion.alumno.numeroAlumno.toLowerCase().includes(searchLower) ||
      inscripcion.curso.nombre.toLowerCase().includes(searchLower) ||
      inscripcion.curso.codigo.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Mostrar mensaje si no hay curso seleccionado (solo para docentes, no para admin)
  if (!selectedCourse && user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Selecciona un curso
          </h3>
          <p className="text-yellow-700">
            Por favor, selecciona un curso desde el selector superior para ver las inscripciones.
          </p>
        </div>
      </div>
    );
  }

  const columns = [
    {
      key: 'alumno',
      label: 'Alumno',
      render: (value: any, row: Inscripcion) => (
        <div>
          <div className="font-medium text-gray-900">
            {row.alumno.nombre} {row.alumno.apellido}
          </div>
          <div className="text-sm text-gray-500">
            N° {row.alumno.numeroAlumno}
          </div>
        </div>
      )
    },
    {
      key: 'curso',
      label: 'Curso',
      render: (value: any, row: Inscripcion) => (
        <div>
          <div className="font-medium text-gray-900">{formatCursoNombreAnio(row.curso)}</div>
          <div className="text-sm text-gray-500">{row.curso.codigo}</div>
        </div>
      )
    },
    {
      key: 'turno',
      label: 'Turno',
      render: (value: any, row: Inscripcion) => (
        <div>
          <div className="font-medium text-gray-900">{row.turno.nombre}</div>
          <div className="text-sm text-gray-500">
            {getDiaLabel(row.turno.dia)} {row.turno.horaInicio}-{row.turno.horaFin}
          </div>
        </div>
      )
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (value: any, row: Inscripcion) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          row.estado === 'activa' 
            ? 'bg-green-100 text-green-800' 
            : row.estado === 'cancelada'
            ? 'bg-red-100 text-red-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {getEstadoLabel(row.estado)}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Fecha',
      render: (value: any, row: Inscripcion) => (
        <div className="text-sm text-gray-900">
          {new Date(row.createdAt).toLocaleDateString()}
        </div>
      )
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (value: any, row: Inscripcion) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(row)}
            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            title="Ver detalles"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          {row.estado === 'activa' && (
            <button
              onClick={() => {
                if (window.confirm('¿Estás seguro de que quieres cancelar esta inscripción?')) {
                  cancelarInscripcionMutation.mutate(row.id);
                }
              }}
              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
              title="Cancelar inscripción"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ];



  const getDiaLabel = (dia: string) => {
    const dias = {
      'LUNES': 'Lun',
      'MARTES': 'Mar',
      'MIERCOLES': 'Mié',
      'JUEVES': 'Jue',
      'VIERNES': 'Vie',
      'SABADO': 'Sáb',
      'DOMINGO': 'Dom'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  const getEstadoLabel = (estado: string) => {
    const estados = {
      'activa': 'Activa',
      'cancelada': 'Cancelada',
      'finalizada': 'Finalizada'
    };
    return estados[estado as keyof typeof estados] || estado;
  };

  if (isLoading) return <div className="text-center py-8">Cargando inscripciones...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error al cargar las inscripciones</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Inscripciones</h2>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              value={filtroCursoId ?? ''}
              onChange={(e) => setFiltroCursoId(e.target.value ? Number(e.target.value) : null)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los cursos</option>
              {Array.isArray(courses) && (courses as Curso[]).map((c) => (
                <option key={c.id} value={c.id}>{formatCursoLabel(c)}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">
              Total: {filteredInscripciones.length} inscripciones
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {(user?.rol === 'admin' || user?.rol === 'jtp') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Agregar nueva inscripción (permite crear incluso si las inscripciones están cerradas)"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Agregar Inscripción
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredInscripciones}
        loading={isLoading}
        emptyMessage="No hay inscripciones registradas"
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: 'Buscar por alumno, curso...'
        }}
      />

      {/* Modal de detalles */}
      <Modal
        isOpen={!!showDetails}
        onClose={() => setShowDetails(null)}
        title="Detalles de la Inscripción"
        size="lg"
      >
        {showDetails && (
          <div className="space-y-6">
            {/* Información del alumno */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Información del Alumno</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Nombre completo</p>
                  <p className="font-medium">{showDetails.alumno.nombre} {showDetails.alumno.apellido}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Número de alumno</p>
                  <p className="font-medium">{showDetails.alumno.numeroAlumno}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{showDetails.alumno.email}</p>
                </div>
              </div>
            </div>

            {/* Información del curso */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Información del Curso</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Nombre del curso</p>
                  <p className="font-medium">{formatCursoNombreAnio(showDetails.curso)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Código</p>
                  <p className="font-medium">{showDetails.curso.codigo}</p>
                </div>
              </div>
            </div>

            {/* Información del turno */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Información del Turno</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Nombre del turno</p>
                  <p className="font-medium">{showDetails.turno.nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Día y horario</p>
                  <p className="font-medium">
                    {getDiaLabel(showDetails.turno.dia)} {showDetails.turno.horaInicio}-{showDetails.turno.horaFin}
                  </p>
                </div>
              </div>
            </div>

            {/* Información de la inscripción */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Información de la Inscripción</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Estado</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    showDetails.estado === 'activa' 
                      ? 'bg-green-100 text-green-800' 
                      : showDetails.estado === 'cancelada'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {getEstadoLabel(showDetails.estado)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fecha de inscripción</p>
                  <p className="font-medium">
                    {new Date(showDetails.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Creada el</p>
                  <p className="font-medium">
                    {new Date(showDetails.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Última actualización</p>
                  <p className="font-medium">
                    {new Date(showDetails.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal para crear inscripción */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Agregar Nueva Inscripción"
        size="lg"
      >
        <div className="space-y-6">
          {/* Buscar alumno */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar Alumno
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Busca por número de legajo, apellido o nombre
            </p>
            <form onSubmit={handleBuscarAlumno} className="flex gap-2">
              <input
                type="text"
                value={terminoBusqueda}
                onChange={(e) => {
                  setTerminoBusqueda(e.target.value);
                  setAlumnosEncontrados([]);
                  setAlumnoSeleccionado(null);
                }}
                placeholder="Ej: 12345/6, Pérez, o Juan"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={buscarAlumnoMutation.isPending || !terminoBusqueda.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {buscarAlumnoMutation.isPending ? 'Buscando...' : 'Buscar'}
              </button>
            </form>
            
            {/* Lista de resultados de búsqueda */}
            {alumnosEncontrados.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 border-b">
                  {alumnosEncontrados.length} resultado{alumnosEncontrados.length > 1 ? 's' : ''} encontrado{alumnosEncontrados.length > 1 ? 's' : ''}
                </div>
                {alumnosEncontrados.map((alumno) => (
                  <button
                    key={alumno.id}
                    type="button"
                    onClick={() => handleSeleccionarAlumno(alumno)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {alumno.apellido}, {alumno.nombre}
                    </p>
                    <p className="text-xs text-gray-600">
                      Legajo: {alumno.numeroAlumno} | Email: {alumno.email}
                    </p>
                  </button>
                ))}
              </div>
            )}

            {errorBusqueda && (
              <p className="mt-2 text-sm text-red-600">{errorBusqueda}</p>
            )}
            
            {alumnoSeleccionado && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {alumnoSeleccionado.apellido}, {alumnoSeleccionado.nombre}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Legajo: {alumnoSeleccionado.numeroAlumno} | Email: {alumnoSeleccionado.email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAlumnoSeleccionado(null);
                      setTerminoBusqueda('');
                    }}
                    className="text-green-700 hover:text-green-900 text-sm"
                  >
                    Cambiar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Seleccionar curso */}
          {alumnoSeleccionado && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Curso
              </label>
              <select
                value={cursoSeleccionado?.id || ''}
                onChange={(e) => {
                  const curso = courses.find(c => c.id === parseInt(e.target.value));
                  setCursoSeleccionado(curso || null);
                  setTurnoSeleccionado(null);
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Selecciona un curso</option>
                {courses.map((curso) => (
                  <option key={curso.id} value={curso.id}>
                    {formatCursoLabel(curso)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Seleccionar turno */}
          {cursoSeleccionado && turnosDisponibles && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Turno
              </label>
              {turnosDisponibles.length === 0 ? (
                <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-md">
                  No hay turnos disponibles para este curso.
                </p>
              ) : (
                <div className="space-y-2">
                  <select
                    value={turnoSeleccionado?.id || ''}
                    onChange={(e) => {
                      const turno = turnosDisponibles.find((t: Turno) => t.id === parseInt(e.target.value));
                      setTurnoSeleccionado(turno || null);
                    }}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un turno</option>
                    {turnosDisponibles.map((turno: Turno) => {
                      const cupoDisponible = turno.cupoMaximo - turno.cupoOcupado;
                      const cupoCompleto = turno.cupoOcupado >= turno.cupoMaximo;
                      const inscripcionesCerradas = !turno.inscripcionesAbiertas;
                      
                      return (
                        <option key={turno.id} value={turno.id}>
                          {turno.nombre} - {getDiaLabel(turno.dia)} {turno.horaInicio}-{turno.horaFin} 
                          ({turno.cupoOcupado}/{turno.cupoMaximo} cupos)
                          {inscripcionesCerradas ? ' [Inscripciones cerradas]' : ''}
                          {cupoCompleto ? ' [Cupo completo]' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {turnoSeleccionado && (
                    <div className={`p-3 rounded-md text-sm ${
                      !turnoSeleccionado.inscripcionesAbiertas 
                        ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                        : turnoSeleccionado.cupoOcupado >= turnoSeleccionado.cupoMaximo
                        ? 'bg-orange-50 border border-orange-200 text-orange-800'
                        : 'bg-green-50 border border-green-200 text-green-800'
                    }`}>
                      {!turnoSeleccionado.inscripcionesAbiertas && (
                        <p className="font-medium">⚠️ Las inscripciones están cerradas para este turno</p>
                      )}
                      {turnoSeleccionado.cupoOcupado >= turnoSeleccionado.cupoMaximo && (
                        <p className="font-medium">⚠️ El cupo está completo ({turnoSeleccionado.cupoOcupado}/{turnoSeleccionado.cupoMaximo})</p>
                      )}
                      {turnoSeleccionado.inscripcionesAbiertas && turnoSeleccionado.cupoOcupado < turnoSeleccionado.cupoMaximo && (
                        <p className="font-medium">✅ Turno disponible - Cupos libres: {turnoSeleccionado.cupoMaximo - turnoSeleccionado.cupoOcupado}</p>
                      )}
                      <p className="text-xs mt-1">
                        Como administrador, puedes crear la inscripción incluso si las inscripciones están cerradas o el cupo está completo.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCrearInscripcion}
              disabled={!alumnoSeleccionado || !cursoSeleccionado || !turnoSeleccionado || crearInscripcionMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {crearInscripcionMutation.isPending ? 'Creando...' : 'Crear Inscripción'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InscripcionesPage;
